import {none, option, Option} from "ts-option";
import LatLng from "../map/LatLng";
import IndividualContact from "../contact/IndividualContact";
import Event from "../event/Event";
import {getLeadType} from "../../store/view/leadType";
import {getAppointmentType} from "../../store/view/appointment";
import {getUser} from "../../store/view/user";
import Appointment from "./Appointment";
import {
	AppointmentApiDto,
	JobAppointmentApiDto,
	JobAppointmentDetailsApiDto,
	LeadAppointmentDetailsApiDto
} from "../../api/model/rest";
import JobAppointment from "./job/JobAppointment";
import User from "../user/User";
import Site from "../site/Site";
import {getJobTypeAsDictionaryItem} from "../../store/view/jobType";
import {DiaryRowAppointment} from "../../components/dayView/DayViewTimeLine";

export type AppointmentDataType = Appointment | JobAppointment

export default class AppointmentDetails {
	constructor(public readonly site: Option<Site>,
		public readonly appointment: AppointmentDataType,
		public readonly location: Option<LatLng>,
		public readonly referenceId: string,
		public readonly typeId: Option<string>,
		public readonly individualContact: IndividualContact,
		public readonly isLeadAppointment: boolean,
	) {}

	public static fromJson(json: LeadAppointmentDetailsApiDto | JobAppointmentDetailsApiDto, isLeadAppointment: boolean) {
		let referenceId;
		let typeId;
		let appointment
		if (isLeadAppointment) {
			const _json = json as LeadAppointmentDetailsApiDto;
			referenceId = _json.leadId;
			typeId = option(_json.leadTypeId);
			appointment = Appointment.fromJson(json.appointment as AppointmentApiDto)
		}
		else {
			const _json = json as JobAppointmentDetailsApiDto;
			referenceId = _json.jobId;
			typeId = option(_json.jobTypeId);
			appointment = JobAppointment.fromJson(json.appointment as JobAppointmentApiDto)
		}
		const address = json.site ? option(Site.fromJson(json.site)) : none
		return new AppointmentDetails(address, appointment, none, referenceId, typeId, IndividualContact.fromJson(json.contact),
			isLeadAppointment)
	}

	toEvent() {
		const salesEngineers: Option<User[]> = this.getSalesEngineers()
		return new Event(this.appointment.id,
			this.appointment.referenceNumber.getOrElse(""),
			`${this.appointment.status.label};${this.site.map(s => ` ${s.data.address.displayName()}`).getOrElse("")}`,
			this.appointment.dateTime.toDate(),
			this.appointment.endDateTime.toDate(),
			this.appointment.status,
			this.individualContact,
			this.site.map(s => s.data.address),
			this.getType(),
			this.referenceId,
			this.appointment.referenceNumber.getOrElse(""),
			getAppointmentType(this.appointment.typeId.orNull),
			salesEngineers,
			this.isLeadAppointment)
	}

	toDiaryData(): DiaryRowAppointment {
		return {
			appointment: this.appointment,
			contact: this.individualContact,
			referenceNumber: this.appointment.referenceNumber,
			address: this.site.map(s => s.data.address),
			type: this.getType(),
			isLeadAppointment: this.isLeadAppointment
		}
	}

	getType() {
		return this.isLeadAppointment ? getLeadType(this.typeId.orNull) : getJobTypeAsDictionaryItem(this.typeId.orNull)
	};

	getSalesEngineers(): Option<User[]> {
		return this.isLeadAppointment ?
			getUser((this.appointment as Appointment).assigneeId.orNull).flatMap(u => u.isSalesEngineer() ? option([u]) : none) :
			option((this.appointment as JobAppointment).assignees.map(uId => getUser(uId)).filter(
				user => user.isDefined && user.get.isSalesEngineer()).map(user => user.get))
	}

	/**
	 * Get reference number.
	 * @returns {string}
	 */
	getReferenceNumber(): string {
		return `${this.isLeadAppointment ? "LEAD" : "JOB"}-${this.appointment.referenceNumber.getOrElse("")}`
	}

	copy({location = this.location}): AppointmentDetails {
		return new AppointmentDetails(
			this.site,
			this.appointment,
			location,
			this.referenceId,
			this.typeId,
			this.individualContact,
			this.isLeadAppointment
		);
	}

	public static compare(a: AppointmentDetails, b: AppointmentDetails) {
		return a.appointment.dateTime.diff(b.appointment.dateTime)
	}
}